import { useCallback, useEffect, useRef, useState } from "react";

/** Audio as it arrives: `type` is the response's content type, `body` the bytes still streaming in. */
export interface SpeechAudio { type: string; body: ReadableStream<Uint8Array> }
/** Text in, audio out. The caller decides where it goes (the workspace API); this package never does. */
export type Synthesize = (text: string) => Promise<SpeechAudio>;
export type PlayerState = "idle" | "fetching" | "speaking";

/** Where audio is played. Injectable, so the queue can be tested without a sound card. */
export interface Sink {
  /** Resolves when the clip has finished playing, or at once when it was stopped. */
  play(audio: SpeechAudio): Promise<void>;
  stop(): void;
  /** Browsers only allow sound after a gesture. Resolves false when the page is still not allowed to play. */
  unlock(): Promise<boolean>;
}

/** How far ahead of the clock a clip's first audio is scheduled: a little in hand against a slow network moment at
 * the start, which is the only place playback can run dry (the provider sends several times faster than speech). */
export const LEAD_S = 0.15;
/** A clip's first and last few milliseconds are ramped, and Stop fades rather than cuts: no clicks at the joins. */
export const FADE_S = 0.005;

/** How playback went, for device testing: chunks played and the times the audio ran dry mid-clip. */
export const playbackStats = { clips: 0, stalls: 0 };

/**
 * When the next buffer starts. Back to back with the last one, unless the clock has caught up with it: then it
 * starts a lead's worth from now, and if that was not the first buffer of the clip, the audio ran dry (a stall).
 */
export function nextStart(at: number, now: number, first: boolean): { at: number; stalled: boolean } {
  if (at >= now + 0.01) return { at, stalled: false };
  return { at: now + LEAD_S, stalled: !first };
}

/** Ramp the first and/or last FADE_S of a buffer in place. */
export function fadeEdges(samples: Float32Array, rate: number, edges: { start?: boolean; end?: boolean }): Float32Array {
  const n = Math.min(Math.round(rate * FADE_S), Math.floor(samples.length / 2));
  for (let i = 0; i < n; i++) {
    const k = i / n;
    if (edges.start) samples[i] = (samples[i] ?? 0) * k;
    if (edges.end) samples[samples.length - 1 - i] = (samples[samples.length - 1 - i] ?? 0) * k;
  }
  return samples;
}
/** Chunks are gathered to at least this much audio before scheduling: fewer, longer buffers, no audible seams. */
const MIN_CHUNK_S = 0.1;

/** 16-bit little-endian samples to floats. An odd trailing byte is handed back to be joined to the next chunk. */
export function pcmToFloat32(bytes: Uint8Array): { samples: Float32Array; rest: Uint8Array } {
  const whole = bytes.length - (bytes.length % 2);
  const view = new DataView(bytes.buffer, bytes.byteOffset, whole);
  const samples = new Float32Array(whole / 2);
  for (let i = 0; i < samples.length; i++) samples[i] = view.getInt16(i * 2, true) / 32768;
  return { samples, rest: bytes.slice(whole) };
}

export function pcmRate(type: string): number | null {
  if (!/^audio\/pcm/i.test(type)) return null;
  return Number(/rate=(\d+)/i.exec(type)?.[1] ?? 24000);
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) { out.set(p, at); at += p.length; }
  return out;
}

/** One clip being played: what `stop()` has to reach to silence it at once. */
interface Clip { stopped: boolean; reader: ReadableStreamDefaultReader<Uint8Array> | null; sources: Set<AudioBufferSourceNode>; wake: (() => void) | null; ctx: AudioContext; out: GainNode }

/** One audio context for the page: once a tap has unlocked sound it stays unlocked as the user moves between
 * screens, instead of each screen's new context needing its own gesture (Safari asks for one per context). */
let shared: AudioContext | null = null;

/**
 * Whether this page may play sound without a tap, found by trying: the page's audio context is created and asked
 * to start. iOS never allows it on a fresh load (not even from the home screen); Chrome allows it for an app
 * installed to the home screen, or a site the person uses often. A context that is not running after a moment
 * counts as not allowed: on iOS the start request simply never answers.
 */
export async function soundAllowed(waitMs = 400): Promise<boolean> {
  try {
    const c = (shared ??= new AudioContext());
    if (c.state === "running") return true;
    await Promise.race([c.resume().catch(() => undefined), new Promise((r) => setTimeout(r, waitMs))]);
    return (c.state as AudioContextState) === "running";   // re-read: resume() may have changed it
  } catch {
    return false;
  }
}

export function webAudioSink(): Sink {
  let current: Clip | null = null;
  const context = () => (shared ??= new AudioContext());

  const play = (c: AudioContext, clip: Clip, buffer: AudioBuffer, at: number) => {
    const src = c.createBufferSource();
    src.buffer = buffer;
    src.connect(clip.out);
    src.onended = () => { clip.sources.delete(src); if (!clip.sources.size) clip.wake?.(); };
    clip.sources.add(src);
    src.start(at);
    return at + buffer.duration;
  };
  /** Resolves once everything scheduled has played, or the clip is stopped. */
  const finished = (clip: Clip) => (clip.sources.size && !clip.stopped ? new Promise<void>((r) => { clip.wake = r; }) : Promise.resolve());

  /** Raw pcm: each chunk is scheduled on the audio clock as it arrives, so speech starts on the first bytes. */
  async function streamPcm(c: AudioContext, clip: Clip, rate: number) {
    let at = 0;
    let parts: Uint8Array[] = [];
    let bytes = 0;
    let first = true;
    playbackStats.clips += 1;
    const schedule = (last: boolean) => {
      const { samples, rest } = pcmToFloat32(concat(parts));
      parts = rest.length ? [rest] : [];
      bytes = rest.length;
      if (!samples.length) return;
      fadeEdges(samples, rate, { start: first, end: last });
      const next = nextStart(at, c.currentTime, first);
      if (next.stalled) {
        playbackStats.stalls += 1;
        console.info(`[voice] playback ran dry mid-clip (${playbackStats.stalls} of ${playbackStats.clips} clips so far)`);
      }
      at = next.at;
      first = false;
      const buffer = c.createBuffer(1, samples.length, rate);
      buffer.copyToChannel(samples, 0);
      at = play(c, clip, buffer, at);
    };
    for (;;) {
      const { done, value } = await clip.reader!.read();
      if (clip.stopped) return;
      if (done) break;
      parts.push(value);
      bytes += value.length;
      if (bytes >= rate * 2 * MIN_CHUNK_S) schedule(false);
    }
    schedule(true);
    await finished(clip);
  }

  /** Any other format plays once it has fully arrived. */
  async function playWhole(c: AudioContext, clip: Clip) {
    const parts: Uint8Array[] = [];
    for (let r = await clip.reader!.read(); !r.done; r = await clip.reader!.read()) parts.push(r.value);
    if (clip.stopped) return;
    const buffer = await c.decodeAudioData(concat(parts).buffer as ArrayBuffer);
    if (clip.stopped) return;
    play(c, clip, buffer, c.currentTime);
    await finished(clip);
  }

  return {
    async play(audio) {
      const c = context();
      if (c.state === "suspended") await c.resume().catch(() => undefined);
      if (c.state !== "running") { void audio.body.cancel().catch(() => undefined); throw new Error("autoplay_blocked"); }
      const out = c.createGain();
      out.connect(c.destination);
      const clip: Clip = { stopped: false, reader: audio.body.getReader(), sources: new Set(), wake: null, ctx: c, out };
      current = clip;
      try {
        const rate = pcmRate(audio.type);
        await (rate ? streamPcm(c, clip, rate) : playWhole(c, clip));
      } finally {
        if (current === clip) current = null;
        setTimeout(() => out.disconnect(), 100);
      }
    },
    stop() {
      const clip = current;
      current = null;
      if (!clip) return;
      clip.stopped = true;
      void clip.reader?.cancel().catch(() => undefined);
      // a quick fade, then silence: cutting a waveform mid-swing is a click
      const now = clip.ctx.currentTime;
      clip.out.gain.setTargetAtTime(0, now, FADE_S / 2);
      clip.sources.forEach((s) => { try { s.stop(now + FADE_S * 4); } catch { /* not started */ } });
      clip.wake?.();
    },
    async unlock() {
      try {
        const c = context();
        await c.resume().catch(() => undefined);
        return c.state === "running";
      } catch {
        return false;   // no Web Audio here at all: the text is still on screen
      }
    },
  };
}

/**
 * A queue of things to say. Each is requested as soon as it is queued, so the next sentence is usually arriving
 * before the current one ends, but they are always played in the order they were queued. `stop()` empties the queue,
 * silences the current clip and cancels the requests still in flight.
 */
export function useSpeechPlayer(synthesize: Synthesize, makeSink: () => Sink = webAudioSink) {
  const [state, setState] = useState<PlayerState>("idle");
  const [blocked, setBlocked] = useState(false);
  const sink = useRef<Sink | null>(null);
  const queue = useRef<Array<Promise<SpeechAudio | null>>>([]);
  const running = useRef(false);
  const generation = useRef(0);   // bumped by stop(): work started before it is thrown away
  const waiters = useRef<Array<() => void>>([]);
  const settle = () => { const w = waiters.current; waiters.current = []; w.forEach((f) => f()); };

  const getSink = useCallback(() => (sink.current ??= makeSink()), [makeSink]);

  const drop = (items: Array<Promise<SpeechAudio | null>>) => items.forEach((p) => void p.then((a) => a?.body.cancel().catch(() => undefined)));

  const drain = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    const gen = generation.current;
    try {
      while (queue.current.length && gen === generation.current) {
        setState("fetching");
        const audio = (await queue.current[0]) ?? null;
        if (gen !== generation.current) { drop([Promise.resolve(audio)]); break; }
        queue.current.shift();
        if (!audio) continue;                       // that sentence failed: the text is on screen, move on
        setState("speaking");
        try {
          await getSink().play(audio);
        } catch (e) {
          if (e instanceof Error && e.message === "autoplay_blocked") {
            setBlocked(true);                       // the page may not play yet; one tap will let it
            drop(queue.current);
            queue.current = [];
            break;
          }
        }
      }
    } finally {
      // a drain ended by stop() leaves the flag to whatever drain started after it
      if (gen === generation.current) { running.current = false; setState("idle"); settle(); }
    }
  }, [getSink]);

  const speak = useCallback((text: string) => {
    const words = text.trim();
    if (!words) return;
    queue.current.push(synthesize(words).catch(() => null));
    void drain();
  }, [synthesize, drain]);

  /** Audio that is already to hand (a cached clip), queued in order with what is being said. */
  const play = useCallback((audio: () => Promise<SpeechAudio>) => {
    queue.current.push(audio().catch(() => null));
    void drain();
  }, [drain]);

  /** Resolves when everything queued has been said, or it was stopped. */
  const whenIdle = useCallback(() => (running.current || queue.current.length ? new Promise<void>((r) => waiters.current.push(r)) : Promise.resolve()), []);

  const stop = useCallback(() => {
    generation.current += 1;
    drop(queue.current);
    queue.current = [];
    sink.current?.stop();
    running.current = false;
    setState("idle");
    settle();
  }, []);

  /** Call from a user gesture (the toggle, the "tap to enable sound" prompt). */
  const unlock = useCallback(async () => {
    const ok = await getSink().unlock();
    setBlocked(!ok);
    return ok;
  }, [getSink]);

  // Leaving is a full stop: the queue, the clip and the "busy" flag. A cleanup that only abandoned the work left the
  // flag set, and a screen mounted again (StrictMode does so in development) stayed "getting ready" for good.
  useEffect(() => () => stop(), [stop]);

  return { speak, play, whenIdle, stop, unlock, state, blocked };
}
